import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import Skeleton from '@mlb/react-patterns/v4/components/Skeleton/skeleton';
import { TeamLogoTypes } from '@mlb/react-patterns/v4/components/TeamLogo/teamLogo.types';
import { useTranslate } from '../../utils/hooks/useTranslate';
import getGameStateBaseLabel from '../../utils/getGameStateLabel';
import getProcessedGameDate from '../../utils/getProcessedGameDate';
import Link from '@mlb/react-patterns/v4/components/Link/link';
import { useMedia } from '@mlb/react-patterns/v4/utils/hooks/useMedia/useMedia';
import GDBREAKPOINTS from '../themes/breakpoints';
import {
    AdditionalTextWrapper,
    GameStateWrapper,
    SkeletonWrapper,
    ScoreWrapper,
    TeamOverride,
    TeamSkeletonWrapper,
    TeamSummaryMatchUpWrapper,
    TeamWrapper,
    GameStateSubtitleWrapper,
    GameStateSubtitleDivider,
    SubtitleLayoutWrapper
} from './TeamSummaryMatchup.style';
import { resourcePath } from '@gameday/utils/resource-path';
import Icon from '@mlb/react-patterns/v4/components/Icon/icon';
import GameDetailsDataContainer from './GameDetailsDataContainer';
import { useTeamUrl } from '@gameday/utils/hooks/useTeamUrl';
import useTracking from '@gameday/utils/hooks/useTracking';

const logoType = TeamLogoTypes.SPOT;
const UNDEF = undefined;

const { SM_MIN } = GDBREAKPOINTS;
const teamSkeleton = (
    <TeamSkeletonWrapper>
        <Skeleton height='50px' width='50px' />
        <Skeleton height='12px' width='50px' />
    </TeamSkeletonWrapper>
);

const scoreSkeleton = <Skeleton height='35px' width='22px' />;

const teamSummarySkeleton = (
    <SkeletonWrapper>
        <Skeleton height='50px' width='50px' />
        <Skeleton height='50px' width='50px' />
        <Skeleton height='50px' width='50px' />
        <Skeleton height='50px' width='50px' />
        <Skeleton height='50px' width='50px' />
    </SkeletonWrapper>
);

const buildSubtitle = (tiebreakerScenario) => {
    let dictKey; let text; let winnerAbbreviation; let params;
    let displayLink = false;
    const translate = useTranslate();

    if (tiebreakerScenario.isActiveTiebreaker) {
        dictKey = 'label_tiebreaker_live';
        text = 'Tiebreaker';
        displayLink = false;
    } else if (tiebreakerScenario.endedInTiebreaker) {
        winnerAbbreviation = tiebreakerScenario.tiebreakerWinner;
        dictKey = 'label_tiebreaker_winner';
        text = `${winnerAbbreviation} wins the tiebreaker`;
        params = { winnerAbbreviation };
    } else {
        return null;
    }
    const subtitleText = translate(dictKey, text, params);

    const { tiebreakerInfo } = tiebreakerScenario;
    displayLink = tiebreakerInfo?.xrefId && tiebreakerInfo?.settingValue === 'homeRunDerby' && tiebreakerInfo?.xrefId;
    const tiebreakerLink = displayLink && resourcePath.get('homeRunDerby', tiebreakerInfo?.xrefId);

    const subtitleComponent = displayLink
        ? (<Link
            url={tiebreakerLink}
            target='_blank'
        >
            <GameStateSubtitleWrapper>
                {subtitleText}
                <Icon name='arrow-next' width={8}/>
            </GameStateSubtitleWrapper>

        </Link>)
        : (<GameStateSubtitleWrapper>{subtitleText}</GameStateSubtitleWrapper>);
    return subtitleComponent;
};

const GameStatusCom = (props) => {
    const {
        isLoading,
        content: {
            base = {},
            extended = {}
        },
        subtitle,
        isStickyScore
    } = props;
    const translate = useTranslate();
    let baseText = '';
    const extendedText = translate(extended.dictkey, extended.text); ;
    if (!base.replacement) {
        baseText = translate(base.dictkey, base.text);
    } else {
        baseText = translate(base.dictkey, base.text, base.replacement);
    }

    if (baseText) {
        if (base.additionalText.length > 0) {
            baseText = baseText + base.spacer + base.additionalText.join(' ');
        }
    }

    return (
        <GameStateWrapper>
            <span>
                {isLoading
                    ? <Skeleton height='18px' width='40px' />
                    : <>
                        <div>{baseText}</div>
                        <AdditionalTextWrapper>{extendedText}</AdditionalTextWrapper>
                        {subtitle}
                    </>
                }
            </span>
            {isStickyScore && <GameDetailsDataContainer />}
        </GameStateWrapper>
    );
};

const TeamSummaryMatchup = ({
    awayWins,
    awayLosses,
    homeWins,
    homeLosses,
    awayRuns,
    homeRuns,
    homeTeamId,
    awayTeamId,
    awayName,
    homeName,
    gameStatus,
    homeStandingsText,
    awayStandingsText,
    currentInning,
    scheduledInnings,
    isPreGameDelay,
    isTBD,
    isWarmup,
    isSuspended,
    isInGameDelay,
    isInstantReplay,
    isManagerChallenge,
    isUmpireReview,
    isPostponed,
    isCancelled,
    isLoading = false,
    isSuspendedResumptionOnDate,
    inningState,
    datetime,
    tz,
    displayRecords = true,
    hideTeamNames = false,
    displayStandings = false,
    logoSize = '32',
    scoreSize = '32px',
    tiebreakerScenario = {},
    displayGameStateSubtitle = false,
    isStickyScore,
    deviceInteraction,
    componentName,
    statusCode,
    awayAllStarStatus,
    homeAllStarStatus
}) => {
    const awayTeamUrl = useTeamUrl(awayTeamId);
    const homeTeamUrl = useTeamUrl(homeTeamId);
    const Tracking = useTracking();
    const translate = useTranslate();

    const handleClubPage = useCallback((e, id) => {
        const cfg = {
            componentName: [componentName, 'TeamSummaryMatchup'],
            text: e.currentTarget.innerText,
            activity: 'Team Logo Link Click'
        };

        Tracking.track.track_click(cfg);

        if (deviceInteraction?.team({ teamId: id })) {
            e.preventDefault();
        }
    }, [Tracking, deviceInteraction, componentName]);

    const awayLinkProps = useMemo(() => {
        if (awayAllStarStatus === 'Y') return null;

        return {
            href: awayTeamUrl || UNDEF,
            target: '_blank',
            handlers: {
                onClick: (e) => handleClubPage(e, awayTeamId)
            },
            ariaLabel: awayName
        };
    }, [awayTeamUrl, awayTeamId, awayName, Tracking]);

    const homeLinkProps = useMemo(() => {
        if (homeAllStarStatus === 'Y') return null;
        return {
            href: homeTeamUrl || UNDEF,
            target: '_blank',
            handlers: {
                onClick: (e) => handleClubPage(e, homeTeamId)
            },
            ariaLabel: homeName
        };
    }, [homeTeamUrl, homeTeamId, homeName, Tracking]);

    const gameTime = getProcessedGameDate({
        datetime,
        isTBD,
        tz
    });

    const inningOrdinal = translate('ranking_' + currentInning, currentInning);

    const contentConfig = getGameStateBaseLabel({
        gameStatus,
        currentInning,
        isPreGameDelay,
        isTBD,
        isWarmup,
        isSuspended,
        isInGameDelay,
        isInstantReplay,
        isManagerChallenge,
        isUmpireReview,
        isPostponed,
        isCancelled,
        inningState,
        isSuspendedResumptionOnDate,
        gameTime,
        scheduledInnings,
        statusCode,
        inningOrdinal,
        shouldUseOrdinal: false
    });

    const gameStateSubtitle = buildSubtitle(tiebreakerScenario);
    const displaySubtitleInline = useMedia({ minWidth: SM_MIN });
    const showInlineSubtitle = displayGameStateSubtitle && displaySubtitleInline && gameStateSubtitle;
    const showSubtitleDivider = displayGameStateSubtitle && !displaySubtitleInline && gameStateSubtitle;
    const hideData = !displayStandings && hideTeamNames && !displayRecords;

    const awayTeamComp = useMemo(() => {
        return isLoading
            ? teamSkeleton
            : (
                <TeamWrapper
                    className={'away'}
                    isLoading={ isLoading }
                    hideData={ hideData }
                    logoSize={ logoSize }
                >
                    <TeamOverride
                        leftAligned={ false }
                        wins={ awayWins }
                        losses={ awayLosses }
                        teamId={ awayTeamId }
                        logoSize={ logoSize }
                        teamLogoType={ logoType }
                        name={ hideTeamNames ? '' : awayName }
                        standingsText={ displayStandings ? awayStandingsText : '' }
                        recordBelow={ true }
                        displayRecord={ displayRecords }
                        alt={ awayName }
                        linkProps={ awayLinkProps }
                    />
                </TeamWrapper>
            );
    }, [isLoading, displayRecords, awayWins, awayLosses, awayTeamId, logoSize, awayName, awayStandingsText, hideTeamNames]);

    const homeTeamComp = useMemo(() => {
        return isLoading
            ? teamSkeleton
            : (
                <TeamWrapper
                    className={'home'}
                    isLoading={ isLoading }
                    hideData={ hideData }
                    logoSize={ logoSize }
                >
                    <TeamOverride
                        leftAligned={ true }
                        wins={ homeWins }
                        losses={ homeLosses }
                        teamId={ homeTeamId }
                        logoSize={ logoSize }
                        teamLogoType={ logoType }
                        name={ hideTeamNames ? '' : homeName }
                        standingsText={ displayStandings ? homeStandingsText : '' }
                        recordBelow={ true }
                        displayRecord={ displayRecords }
                        alt={ homeName }
                        linkProps={ homeLinkProps }
                    />
                </TeamWrapper>
            );
    }, [isLoading, displayRecords, homeWins, homeLosses, homeTeamId, logoSize, homeName, homeStandingsText, hideTeamNames]);

    if (isLoading) {
        return teamSummarySkeleton;
    };

    return (
        <>
            <TeamSummaryMatchUpWrapper displayRecords={displayRecords} $isStickyScore={isStickyScore}>
                { awayTeamComp }
                <ScoreWrapper className={'away'} scoreSize={ scoreSize }>
                    <span>{ isLoading ? scoreSkeleton : awayRuns }</span>
                </ScoreWrapper>
                <GameStatusCom
                    isLoading={isLoading}
                    content={contentConfig}
                    subtitle={showInlineSubtitle && gameStateSubtitle}
                    isStickyScore={isStickyScore}
                />
                <ScoreWrapper className={'home'} scoreSize={ scoreSize }>
                    <span>{ isLoading ? scoreSkeleton : homeRuns }</span>
                </ScoreWrapper>
                { homeTeamComp }
            </TeamSummaryMatchUpWrapper>
            {showSubtitleDivider &&
            <SubtitleLayoutWrapper>
                <GameStateSubtitleDivider/>
                {gameStateSubtitle}
            </SubtitleLayoutWrapper>
            }
        </>

    );
};

TeamSummaryMatchup.propTypes = {
    awayWins: PropTypes.number,
    awayLosses: PropTypes.number,
    homeWins: PropTypes.number,
    homeLosses: PropTypes.number,
    awayRuns: PropTypes.number,
    homeRuns: PropTypes.number,
    homeTeamId: PropTypes.number,
    awayTeamId: PropTypes.number,
    gameStatus: PropTypes.string,
    awayName: PropTypes.string,
    homeName: PropTypes.string,
    homeStandingsText: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    awayStandingsText: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    isLoading: PropTypes.bool,
    currentInning: PropTypes.number,
    scheduledInnings: PropTypes.number,
    inningState: PropTypes.string,
    datetime: PropTypes.object,
    tz: PropTypes.string,
    isPreGameDelay: PropTypes.bool,
    isTBD: PropTypes.bool,
    isWarmup: PropTypes.bool,
    isSuspended: PropTypes.bool,
    isInGameDelay: PropTypes.bool,
    isInstantReplay: PropTypes.bool,
    isManagerChallenge: PropTypes.bool,
    isUmpireReview: PropTypes.bool,
    isPostponed: PropTypes.bool,
    isCancelled: PropTypes.bool,
    isSuspendedResumptionOnDate: PropTypes.bool,
    content: PropTypes.object,
    displayRecords: PropTypes.bool,
    hideTeamNames: PropTypes.bool,
    displayStandings: PropTypes.bool,
    scoreSize: PropTypes.string,
    logoSize: PropTypes.string,
    tiebreakerScenario: PropTypes.object,
    displayGameStateSubtitle: PropTypes.bool,
    isStickyScore: PropTypes.bool,
    deviceInteraction: PropTypes.object,
    statusCode: PropTypes.string,
    componentName: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string)
    ]),
    awayAllStarStatus: PropTypes.string,
    homeAllStarStatus: PropTypes.string
};

GameStatusCom.propTypes = {
    isLoading: PropTypes.bool,
    content: PropTypes.object,
    subtitle: PropTypes.node,
    isStickyScore: PropTypes.bool
};

export default TeamSummaryMatchup;
